import React, { useRef, useEffect, useLayoutEffect } from 'react';

/**
 * Dynamisk anteckningskort-komponent med autofokus
 * Används i EstimatPage för intervjufrågor/anteckningar
 */
const DynamicNoteCard = ({ 
  note, 
  shouldFocus, 
  onFocused, 
  onTitleChange, 
  onTitleSubmit, 
  onContentChange, 
  onContentSave,
  onDelete, 
  onToggleEdit 
}) => {
  const titleRef = useRef(null);
  const contentRef = useRef(null);
  
  // Auto-resize textarea efter innehåll
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [note.content]);

  useEffect(() => {
    if (shouldFocus) {
      // Om det är en "Ny anteckning" (isEditing = true), fokusera på rubrik
      // Om det är en badge-anteckning (isEditing = false), fokusera på innehåll
      if (note.isEditing && titleRef.current) {
        titleRef.current.focus();
      } else if (!note.isEditing && contentRef.current) {
        contentRef.current.focus();
      }
      onFocused(); // Markera att fokus har satts
    }
  }, [shouldFocus, note.isEditing, onFocused]);

  return (
    <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          {note.isEditing ? (
            <input
              ref={titleRef}
              type="text"
              value={note.title}
              onChange={(e) => onTitleChange(e.target.value)}
              onBlur={onTitleSubmit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onTitleSubmit();
                }
              }}
              placeholder="Skriv en rubrik..."
              className="w-full px-2 py-1 text-sm font-semibold border border-gray-300 rounded focus:ring-2 focus:ring-leadme-pink focus:border-leadme-pink"
            />
          ) : (
            <h4 
              className="text-sm font-semibold text-gray-800 cursor-pointer hover:text-leadme-pink"
              onClick={onToggleEdit}
            >
              {note.title || 'Klicka för att lägga till rubrik'}
            </h4>
          )}
        </div>
        <button
          onClick={onDelete}
          className="ml-2 text-red-500 hover:text-red-700 text-sm"
          title="Ta bort anteckning"
        >
          ×
        </button>
      </div>
      <textarea
        ref={contentRef}
        value={note.content}
        onChange={(e) => onContentChange(e.target.value)}
        onBlur={onContentSave}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && e.ctrlKey) {
            onContentSave();
          }
        }}
        placeholder="Skriv din anteckning här... (Sparas automatiskt när du lämnar fältet eller trycker Ctrl+Enter)"
        rows={3}
        style={{ resize: 'none', overflow: 'hidden' }}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-leadme-pink text-sm"
      />
    </div>
  );
};

export default DynamicNoteCard;
